/// <amd-module name="Test/Analytics/TrendChartCommon/TrendChartHelper" />
import * as CommonChartSeries from "Analytics/Shared/ChartSeries/ChartSeries";
import { PassPercentSeries } from "Analytics/Shared/ChartSeries/PassPercentSeries";
import { adjustDataForAllPeriod, IDataForDate } from "Analytics/Shared/Common/Utils/DateAdjuster";
import * as SharedTrendChartHelper from "Analytics/Shared/TrendChart/Common/TrendChartHelper";
import { ReportConfigurationDefinitions } from "Test/Analytics/Common/Definitions";
import * as CommonTypes from "Test/Analytics/Common/Types";
import * as ChartSeries from "Test/Analytics/TrendChartCommon/ChartSeries";
import * as Resources from "Test/Analytics/TrendChartCommon/Resources";
import { ITrendChartAggregateData, ITrendChartData } from "Test/Analytics/TrendChartCommon/Types";
import { IFilter } from "Test/common-util/Contracts/Filters";
import { ChartProps, ChartTypesConstants, DataSeries, LabelFormatModes } from "VSS/Features/Charts/Contracts";

export class TrendChartHelper {
    private _emptyString = "";
    public getChartOptions(chartData: ITrendChartData, testInsightsTrendChart: boolean): any {
        const chartOptions = {
            legend: { stackVertically: true },
            suppressAnimation: true,
            specializedOptions: {
                includeMarkers: true
            }
        } as ChartProps;

        this.adjustDataForEveryPeriod(chartData);

        return { ...chartOptions, ...this._getCommonChartOptions(chartData, testInsightsTrendChart) };
    }

    public adjustDataForEveryPeriod(chartData: ITrendChartData): void {
        chartData.primaryChartData = this._adjustDataForAllStackBySeries(chartData.primaryChartData, chartData.confValues);
        if (chartData.secondaryChartData != null) {
            chartData.secondaryChartData = this._adjustDataForAllStackBySeries(
                { [this._emptyString]: chartData.secondaryChartData },
                chartData.confValues
            )[this._emptyString];
        }
    }

    private _adjustDataForAllStackBySeries(
        stackByToAggrMap: { [key: string]: ITrendChartAggregateData[] },
        confValues?: CommonTypes.IReportConfiguration
    ): { [key: string]: ITrendChartAggregateData[] } {
        if (!stackByToAggrMap) {
            return stackByToAggrMap;
        }

        Object.keys(stackByToAggrMap).forEach(stackBy => {
            stackByToAggrMap[stackBy] = this._adjustDataForAllPeriod(stackByToAggrMap[stackBy], confValues);
        });

        return stackByToAggrMap;
    }

    private _adjustDataForAllPeriod(data: ITrendChartAggregateData[], confValues?: CommonTypes.IReportConfiguration): ITrendChartAggregateData[] {
        if (!data || data.length <= 0) {
            return data;
        }

        let adjustableData = data.map(datum => {
            return { date: datum.date, data: datum.aggrValue } as IDataForDate<number>;
        });

        adjustableData = adjustDataForAllPeriod(adjustableData, confValues!.trendBy, confValues!.period, null);

        const adjustedData = adjustableData.map(datum => {
            return { date: datum.date, aggrValue: datum.data } as ITrendChartAggregateData;
        });

        return adjustedData;
    }

    private _getCommonChartOptions(chartData: ITrendChartData, testInsightsTrendChart: boolean): any {
        if (chartData.confValues) {
            switch (chartData.confValues.groupBy) {
                case CommonTypes.GroupBy.None:
                    return this._getCommonSimpleChartOptions(chartData, testInsightsTrendChart);
                case CommonTypes.GroupBy.Container:
                case CommonTypes.GroupBy.Owner:
                case CommonTypes.GroupBy.Priority:
                case CommonTypes.GroupBy.Environment:
                case CommonTypes.GroupBy.Branch:
                case CommonTypes.GroupBy.TestRun:
                    return this._getCommonStackedChartOptions(chartData, testInsightsTrendChart);
                default:
                    return this._getCommonSimpleChartOptions(chartData, testInsightsTrendChart);
            }
        }
        return null;
    }

    private _getCommonSimpleChartOptions(chartData: ITrendChartData, testInsightsTrendChart: boolean): any {
        if (testInsightsTrendChart) {
            return this._getCommonStackedChartOptions(chartData, testInsightsTrendChart);
        }

        const primaryChartSeries = this._getPrimaryChartSeries(
            chartData.primaryChartData[this._emptyString].map(d => d.aggrValue),
            chartData.confValues ? chartData.confValues.outcomes : [CommonTypes.TestOutcome.Failed],
            chartData.metricSelected
        );
        const secondaryChartSeries = chartData.secondaryChartData
            ? this._getSecondaryChartSeries(chartData.secondaryChartData.map(d => d.aggrValue))
            : undefined;
        let secondaryAxisLabelFormatMode = LabelFormatModes.Linear;

        if (chartData.metricSelected === CommonTypes.Metric.ResultCountAndPassRate) {
            secondaryAxisLabelFormatMode = LabelFormatModes.Percentage;
        }

        const helper = new SharedTrendChartHelper.TrendChartHelper();
        const enableTick = !(chartData.metricSelected === CommonTypes.Metric.ResultCountAndPassRate);
        return helper.getCommonChartOptions(
            chartData.primaryChartData,
            primaryChartSeries,
            secondaryAxisLabelFormatMode,
            chartData.secondaryChartData,
            secondaryChartSeries,
            enableTick,
            enableTick
        );
    }

    private _getCommonStackedChartOptions(chartData: ITrendChartData, testInsightsTrendChart: boolean): any {
        let primaryChartSeries = this._getPrimaryChartSeries(
            [],
            chartData.confValues ? chartData.confValues.outcomes : [CommonTypes.TestOutcome.Total],
            chartData.metricSelected
        );
        let xAxisLabelValues: string[] = [];

        const dataSeries: DataSeries[] = [];
        Object.keys(chartData.primaryChartData).forEach(stackBy => {
            primaryChartSeries = this._getPrimaryChartSeries(
                chartData.primaryChartData[stackBy].map(d => d.aggrValue),
                testInsightsTrendChart
                    ? [this._getOutcomeFromStackByValue(stackBy)]
                    : chartData.confValues
                    ? chartData.confValues.outcomes
                    : [CommonTypes.TestOutcome.Total],
                chartData.metricSelected
            );
            dataSeries.push({ name: stackBy, color: testInsightsTrendChart ? primaryChartSeries.color : undefined, data: primaryChartSeries.values });
            // Getting any stackValue's data here
            if (xAxisLabelValues.length === 0) {
                xAxisLabelValues = chartData.primaryChartData[stackBy].map(d => d.date);
            }
        });

        let outcomes = { values: [] } as IFilter;
        if (chartData.confValues!.configuredFilters && chartData.confValues!.configuredFilters[CommonTypes.Filter.Outcome.toString()]) {
            outcomes = chartData.confValues!.configuredFilters[CommonTypes.Filter.Outcome.toString()];
        }

        return {
            chartType: ChartTypesConstants.StackedColumn,
            xAxis: {
                labelFormatMode: LabelFormatModes.Textual,
                labelsEnabled: true,
                labelValues: xAxisLabelValues
            },
            yAxis: {
                allowDecimals: primaryChartSeries.allowDecimal,
                labelFormatMode: LabelFormatModes.Linear,
                labelsEnabled: true,
                markingsEnabled: false,
                title: testInsightsTrendChart && outcomes && outcomes.values.length !== 1 ? Resources.ResultCountText : primaryChartSeries.name,
                min: primaryChartSeries.minValue,
                max: primaryChartSeries.maxValue
            },
            yAxisSecondary: null,
            series: dataSeries
        } as any;
    }

    private _getPrimaryChartSeries(
        seriesData: number[],
        testOutcome: CommonTypes.TestOutcome[],
        chartMetric?: CommonTypes.Metric
    ): CommonChartSeries.ChartSeries {
        switch (chartMetric) {
            case CommonTypes.Metric.ResultCountAndPassRate:
            case CommonTypes.Metric.ResultCount:
                if (testOutcome.length === 0) {
                    // No outcome selected then return all outcome data.
                    return new ChartSeries.TotalTestsSeries(seriesData);
                }

                if (testOutcome.length === 1) {
                    // When one outcome selected then return that outcome data.
                    switch (testOutcome[0]) {
                        case CommonTypes.TestOutcome.Failed:
                            return new ChartSeries.FailedTestsSeries(seriesData);
                        case CommonTypes.TestOutcome.Passed:
                            return new ChartSeries.PassedTestsSeries(seriesData);
                        case CommonTypes.TestOutcome.Inconclusive:
                            return new ChartSeries.InconclusiveTestsSeries(seriesData);
                        case CommonTypes.TestOutcome.Aborted:
                            return new ChartSeries.AbortedTestsSeries(seriesData);
                        case CommonTypes.TestOutcome.NotExecuted:
                            return new ChartSeries.NotExecutedTestsSeries(seriesData);
                        case CommonTypes.TestOutcome.NotImpacted:
                            return new ChartSeries.NotImpactedTestsSeries(seriesData);
                        case CommonTypes.TestOutcome.Error:
                            return new ChartSeries.ErrorTestsSeries(seriesData);
                    }
                }

                const data1 = new ReportConfigurationDefinitions().getOutcomeConfigurationProps().options[testOutcome[0]];
                const data2 = (testOutcome.length - 1).toString();
                // When multiple outcome selected
                return new ChartSeries.MultipleOutcomeTestsSeries(seriesData, { data1: data1, data2: data2 });
            case CommonTypes.Metric.AvgDuration:
                return new ChartSeries.DurationSeries(seriesData);
        }
        return new ChartSeries.TotalTestsSeries(seriesData);
    }

    private _getSecondaryChartSeries(seriesData: number[]): CommonChartSeries.ChartSeries {
        // Currently for rate we are showing pass rate.
        return new PassPercentSeries(seriesData);
    }

    private _getOutcomeFromStackByValue(stackBy: string): CommonTypes.TestOutcome {
        switch (stackBy) {
            case CommonTypes.TestOutcome[CommonTypes.TestOutcome.Failed]:
                return CommonTypes.TestOutcome.Failed;
            case CommonTypes.TestOutcome[CommonTypes.TestOutcome.Passed]:
                return CommonTypes.TestOutcome.Passed;
            case CommonTypes.TestOutcome[CommonTypes.TestOutcome.Inconclusive]:
                return CommonTypes.TestOutcome.Inconclusive;
            case CommonTypes.TestOutcome[CommonTypes.TestOutcome.Aborted]:
                return CommonTypes.TestOutcome.Aborted;
            case CommonTypes.TestOutcome[CommonTypes.TestOutcome.NotExecuted]:
                return CommonTypes.TestOutcome.NotExecuted;
            case CommonTypes.TestOutcome[CommonTypes.TestOutcome.NotImpacted]:
                return CommonTypes.TestOutcome.NotImpacted;
            case CommonTypes.TestOutcome[CommonTypes.TestOutcome.Error]:
                return CommonTypes.TestOutcome.Error;
        }
        return 0;
    }
}
